/**
 * IST (India Standard Time, UTC+5:30) helpers.
 *
 * DB stores date/time as naive IST wall-clock (no timezone suffix).
 * If the browser is already IST, values pass through unchanged.
 * Otherwise:
 *   - SHOW: IST  -> browser local
 *   - SAVE: local -> IST
 */

const IST_TIMEZONE = "Asia/Kolkata";
const IST_OFFSET_MINUTES = 330; // UTC+5:30

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** True when the user's browser is already on Indian time. */
export function isBrowserIst() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz === "Asia/Kolkata" || tz === "Asia/Calcutta") return true;
  } catch (e) {
    // ignore
  }
  // getTimezoneOffset is minutes behind UTC; IST is -330
  return new Date().getTimezoneOffset() === -IST_OFFSET_MINUTES;
}

function normalizeTime(timeStr) {
  if (timeStr == null || timeStr === "") return "00:00:00";
  const parts = String(timeStr).trim().split(":");
  const hh = pad2(parts[0] || "0");
  const mm = pad2(parts[1] || "0");
  const ss = pad2((parts[2] || "0").slice(0, 2));
  return `${hh}:${mm}:${ss}`;
}

/**
 * Accepts YYYY-MM-DD, DD-MM-YYYY, YYYY/MM/DD, Date, or ISO string.
 * Returns YYYY-MM-DD or null.
 */
export function toYmd(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date && !isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(
      value.getDate()
    )}`;
  }
  const str = String(value).trim();
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(
      parsed.getDate()
    )}`;
  }
  return null;
}

/** Build a real Date from naive IST date + time strings. */
function istWallClockToDate(dateStr, timeStr) {
  const ymd = toYmd(dateStr);
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  const [hh, mm, ss] = normalizeTime(timeStr).split(":").map(Number);
  // Treat the given clock as IST, then convert to UTC ms
  const utcMs =
    Date.UTC(y, m - 1, d, hh, mm, ss) - IST_OFFSET_MINUTES * 60 * 1000;
  return new Date(utcMs);
}

function formatInTimeZone(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || "00";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${hour}:${get("minute")}:${get("second")}`,
    hour: Number(hour),
    minute: Number(get("minute")),
    second: Number(get("second")),
  };
}

function splitLocalDate(dateObj) {
  return {
    date: `${dateObj.getFullYear()}-${pad2(dateObj.getMonth() + 1)}-${pad2(
      dateObj.getDate()
    )}`,
    time: `${pad2(dateObj.getHours())}:${pad2(dateObj.getMinutes())}:${pad2(
      dateObj.getSeconds()
    )}`,
    hour: dateObj.getHours(),
    minute: dateObj.getMinutes(),
    second: dateObj.getSeconds(),
  };
}

/**
 * SHOW: convert DB IST date+time into the browser's local date/time.
 * If browser is IST, returns the same values.
 *
 * @param {string|Date} dateStr  DB date (YYYY-MM-DD / DD-MM-YYYY)
 * @param {string} [timeStr]     DB time (HH:mm or HH:mm:ss)
 * @returns {{ date: string, time: string, dateObj: Date|null, skipped: boolean }}
 */
export function istToLocal(dateStr, timeStr = "00:00:00") {
  const ymd = toYmd(dateStr);
  const time = normalizeTime(timeStr);
  if (!ymd) {
    return { date: dateStr || "", time, dateObj: null, skipped: true };
  }
  if (isBrowserIst()) {
    const [y, m, d] = ymd.split("-").map(Number);
    const [hh, mm, ss] = time.split(":").map(Number);
    return {
      date: ymd,
      time,
      dateObj: new Date(y, m - 1, d, hh, mm, ss),
      skipped: true,
    };
  }
  const dateObj = istWallClockToDate(ymd, time);
  const local = splitLocalDate(dateObj);
  return { ...local, dateObj, skipped: false };
}

/**
 * SAVE: convert a local date+time into IST strings for the DB.
 * If browser is IST, returns the same values.
 *
 * @param {string|Date} dateStr
 * @param {string} [timeStr]
 * @returns {{ date: string, time: string, skipped: boolean }}
 */
export function localToIst(dateStr, timeStr = "00:00:00") {
  const ymd = toYmd(dateStr);
  const time = normalizeTime(timeStr);
  if (!ymd) {
    return { date: dateStr || "", time, skipped: true };
  }
  if (isBrowserIst()) {
    return { date: ymd, time, skipped: true };
  }
  const [y, m, d] = ymd.split("-").map(Number);
  const [hh, mm, ss] = time.split(":").map(Number);
  const localDate = new Date(y, m - 1, d, hh, mm, ss);
  const ist = formatInTimeZone(localDate, IST_TIMEZONE);
  return { date: ist.date, time: ist.time, skipped: false };
}

/** SHOW helper for auction `dt` + `atm`. */
export function showAuctionDateTime(dt, atm) {
  return istToLocal(dt, atm || "00:00:00");
}

/** SAVE helper for auction date + time picker values. */
export function saveAuctionDateTime(dt, atm) {
  return localToIst(dt, atm || "00:00:00");
}

/** Format IST DB date for UI (e.g. "14 Aug 2026"). */
export function formatIstDate(dateStr, locale = "en-GB", options) {
  const converted = istToLocal(dateStr, "00:00:00");
  if (!converted.dateObj) return dateStr || "";
  return converted.dateObj.toLocaleDateString(
    locale,
    options || { day: "numeric", month: "short", year: "numeric" }
  );
}

/** Format IST DB time for UI (e.g. "14:30"). */
export function formatIstTime(dateStr, timeStr, withSeconds = false) {
  const converted = istToLocal(dateStr, timeStr);
  if (!converted.time) return timeStr || "";
  return withSeconds ? converted.time : converted.time.slice(0, 5);
}
